import { useQueryClient } from '@tanstack/react-query';
import { useGet, useQueryWithErrorHandling } from '@app/sdk';
import { useAccountIdContext } from '../../components/account/AccountIdContext';

type AuthorisationsResponse = {
  authorisations: AuthorisationResult[];
};

export type AuthorisationResult = {
  id: string;
  authorisationId: string | null;
  authorisationDate: string | null;
  amount: Amount;
  remainingFloat: number | null;
  status: string;
  expiryDate: string | null;
  description: string | null;
  declineReason: string | null;
};

export type AuthorisationStatus = 'Pending' | 'Declined';

function useSearchService() {
  const get = useGet(true, 'ams');

  function getAccountAuthorisations(accountId: string, status?: AuthorisationStatus) {
    const statusQuery = status ? `?status=${encodeURIComponent(status)}` : '';

    return get<AuthorisationsResponse>(
      `/api/proxy/ams/search-service/account/${accountId}/authorisations${statusQuery}`,
      {
        'Api-Version': 2,
      },
    );
  }

  return {
    getAccountAuthorisations,
  };
}

export function useGetAccountAuthorisations({
  accountId,
  status,
}: {
  accountId: string;
  status?: AuthorisationStatus;
}) {
  const { getAccountAuthorisations } = useSearchService();

  return useQueryWithErrorHandling({
    queryKey: ['getAccountAuthorisations', accountId, status],
    queryFn: () => getAccountAuthorisations(accountId, status),
    enabled: !!accountId,
    select: (data) => data.authorisations,
  });
}

export function useRefreshAccountAuthorisations() {
  const { accountId } = useAccountIdContext();
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: ['getAccountAuthorisations', accountId],
    });
}
